import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { PageHeader } from "./shared/components";
import {
  getUserCapabilities,
  getResignationPrefill,
  getMyResignations,
  hasActiveHrInitiatedCase,
  hasBlockingSeparation,
  EsepCapabilities,
  ResignationPrefill,
  SeparationListItem,
  EsepPendingCounts,
  EMPTY_ESEP_PENDING_COUNTS,
  getEsepPendingCounts,
} from "./services/eSeparation.service";

type RoleKey = "Employee" | "Approver" | "HR Admin" | "Nodal" | "Vigilance" | "Finance";

interface OptionCard {
  label: string;
  description: string;
  icon: string;
  accentColor: string;
  iconBg: string;
  route: string;
  category: "employee" | "approver" | "hr" | "clearance";
}

const ALL_ROLES: RoleKey[] = ["Employee", "Approver", "HR Admin", "Nodal", "Vigilance", "Finance"];

const ROLE_STORAGE_KEY = "esep_hub_role";

const EseprationHub: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const [role, setRoleState] = useState<RoleKey>(
    () => (sessionStorage.getItem(ROLE_STORAGE_KEY) as RoleKey) || "Employee"
  );

  const setRole = (r: RoleKey) => {
    setRoleState(r);
    sessionStorage.setItem(ROLE_STORAGE_KEY, r);
  };
  // Single capabilities object drives all tab/card gating — replaces the
  // previous 4 separate useEffects and 8 state variables.
  const [caps, setCaps] = useState<EsepCapabilities | null>(null);
  // Retirement/Superannuation/Deputation gating flags (from the resignation
  // prefill API — self-service, always the caller's own data).
  const [prefill, setPrefill] = useState<ResignationPrefill | null>(null);
  const [requests, setRequests] = useState<SeparationListItem[]>([]);
  // What is waiting on this user, per role tab and per card (refreshed every minute).
  const [pending, setPending] = useState<EsepPendingCounts>(EMPTY_ESEP_PENDING_COUNTS);
  useEffect(() => {
    let alive = true;
    const load = () => getEsepPendingCounts().then((c) => alive && setPending(c));
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  const financeCount = pending.finance_nodal + pending.recovery_head + pending.establishment_manager;
  const roleCount: Record<RoleKey, number> = {
    Employee: 0,
    Approver: pending.approvals + pending.competent_authority,
    "HR Admin": pending.hr,
    Nodal: pending.department,
    Vigilance: pending.vigilance,
    Finance: financeCount,
  };
  // What each card's own screen has waiting for this user.
  const cardCount: Record<string, number> = {
    "Pending Approvals": pending.approvals + pending.competent_authority,
    "HR Verification & Processing": pending.hr_verification,
    "HR No-Dues Final Review": pending.hr_final_review,
    "Initiated Cases": pending.hr_cases,
    "No-Dues Dept Clearance": pending.department,
    "Vigilance Clearance": pending.vigilance,
    "Finance Settlement Action": financeCount,
  };
  const CountBadge: React.FC<{ n: number }> = ({ n }) =>
    n > 0 ? (
      <span
        className="badge bg-danger text-white rounded-pill ms-2"
        style={{ fontSize: 11, padding: "2px 6px" }}
        title={`${n} pending`}
      >
        {n > 99 ? "99+" : n}
      </span>
    ) : null;

  useEffect(() => {
    let mounted = true;
    getUserCapabilities().then((c) => {
      if (!mounted) return;
      setCaps(c);
      // If the stored role is no longer available after capabilities load,
      // fall back to Employee.
      const availableRoles: RoleKey[] = ALL_ROLES.filter((r) => {
        if (r === "HR Admin") return c.roles.is_hr;
        if (r === "Approver") return c.roles.is_approver;
        if (r === "Vigilance") return c.roles.is_vigilance_officer;
        // A sub-nodal gets the same Nodal clearance UI as the department's own
        // approver — they're handling a delegated case, not owning the department.
        if (r === "Nodal") return c.roles.is_dept_approver || c.roles.is_sub_nodal_approver;
        if (r === "Finance")
          return c.roles.is_finance_approver || c.roles.is_recovery_head || c.roles.is_estt_manager;
        return true;
      });
      if (!availableRoles.includes(role)) setRole("Employee");
    });
    getResignationPrefill().then((res) => {
      if (mounted && res.data) setPrefill(res.data);
    });
    getMyResignations().then((res) => {
      if (mounted && res.data) setRequests(res.data);
    });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roles: RoleKey[] = ALL_ROLES.filter((r) => {
    if (r === "HR Admin") return caps?.roles.is_hr ?? false;
    if (r === "Approver") return caps?.roles.is_approver ?? false;
    if (r === "Vigilance") return caps?.roles.is_vigilance_officer ?? false;
    if (r === "Nodal")
      return (caps?.roles.is_dept_approver ?? false) || (caps?.roles.is_sub_nodal_approver ?? false);
    if (r === "Finance")
      return (
        (caps?.roles.is_finance_approver ?? false) ||
        (caps?.roles.is_recovery_head ?? false) ||
        (caps?.roles.is_estt_manager ?? false)
      );
    return true;
  });

  const emp = caps?.employee;

  const getDisabledInfo = (
    route: string,
    category?: OptionCard["category"]
  ): { disabled: boolean; message: string } => {
    // A termination / death case is handled entirely by HR: every employee-side tab
    // (incl. profile / biodata) is closed — nothing to view, nothing to download.
    if (emp?.access_locked && category === "employee") {
      return {
        disabled: true,
        message:
          emp.access_locked_reason ||
          "This separation is handled by HR. E-Separation is not available to you.",
      };
    }
    if (route === routes.Esepration.intimationResignation && prefill?.isRetirement) {
      return {
        disabled: true,
        message:
          "You are due for retirement — resignation cannot be initiated. Please use \"Initiate No-Dues Separation\" to start your No-Dues clearance.",
      };
    }
    const hasActiveCase = hasActiveHrInitiatedCase(requests);
    const hasResignation = hasBlockingSeparation(requests, "RESIGNATION");

    if (
      route === routes.Esepration.intimationResignation &&
      ((emp && !emp.can_submit_resignation) || hasActiveCase || hasResignation)
    ) {
      return {
        disabled: true,
        message:
          emp?.resignation_block_reason ??
          (hasActiveCase
            ? "A separation case is already in progress for you."
            : "A resignation request is already in process."),
      };
    }
    if (route === routes.Esepration.shortNotice && emp && !emp.can_submit_short_notice) {
      return {
        disabled: true,
        message: emp.short_notice_block_reason ?? "A short notice request is already in process.",
      };
    }
    if (route === routes.Esepration.withdrawal && emp && !emp.can_submit_withdrawal) {
      return {
        disabled: true,
        message: emp.withdrawal_block_reason ?? "A withdrawal request is already in process.",
      };
    }
    if (
      route === routes.Esepration.noDuesCaseInitiate &&
      ((emp && !emp.can_initiate_no_dues_case) || hasActiveCase || hasResignation)
    ) {
      return {
        disabled: true,
        message:
          emp?.no_dues_case_block_reason ??
          "A separation request is already in process; you cannot initiate another.",
      };
    }
    if (route === routes.Esepration.noDuesForm && emp && !emp.no_dues_form_enabled) {
      return {
        disabled: true,
        message:
          "No-Dues form becomes available once your resignation reaches HR, or once you initiate a No-Dues separation.",
      };
    }
    if (route === routes.Esepration.noDuesForm && emp?.no_dues_form_submitted) {
      return {
        disabled: true,
        message:
          "You have already submitted your No-Dues form. It is now moving through the approval chain.",
      };
    }
    // Retiring employees skip the normal resignation-must-reach-HR gate —
    // No-Dues is available to them directly.
    if (
      route === routes.Esepration.noDues &&
      emp &&
      !emp.no_dues_dispatched &&
      !prefill?.isRetirement
    ) {
      return {
        disabled: true,
        message:
          "Department-wise No-Dues clearance tracking opens once your No-Dues reaches HR and is sent to the departments for clearance.",
      };
    }
    return { disabled: false, message: "" };
  };

  const showDisabled = (message: string) => {
    toast.warning(
      <div>
        <div className="fw-semibold">Please Wait</div>
        <div className="small">{message}</div>
      </div>
    );
  };

  const cards: OptionCard[] = [
    {
      label: "Overview & Status",
      description: "Dashboard with all your separation requests and status",
      icon: "ti-layout-dashboard",
      accentColor: "#185FA5",
      iconBg: "#E5F0FA",
      route: routes.Esepration.overview,
      category: "employee",
    },
    {
      label: "Intimation & Resignation",
      description: "Submit resignation intimation with 90-day notice",
      icon: "ti-file-text",
      accentColor: "#0F6E56",
      iconBg: "#E2F3EE",
      route: routes.Esepration.intimationResignation,
      category: "employee",
    },
    {
      label: "Initiate No-Dues Separation",
      description:
        "Retirement, deputation or transfer-out — No-Dues process only",
      icon: "ti-file-check",
      accentColor: "#4A1A6E",
      iconBg: "#F0E7F6",
      route: routes.Esepration.noDuesCaseInitiate,
      category: "employee",
    },
    {
      label: "Short Notice Resignation",
      description: "Request early relieving before notice period ends",
      icon: "ti-clock-bolt",
      accentColor: "#BA7517",
      iconBg: "#FFF1DE",
      route: routes.Esepration.shortNotice,
      category: "employee",
    },
    {
      label: "Withdrawal of Resignation",
      description: "Cancel a previously submitted resignation request",
      icon: "ti-arrow-back-up",
      accentColor: "#993C1D",
      iconBg: "#FBE9E2",
      route: routes.Esepration.withdrawal,
      category: "employee",
    },
    {
      label: "Submit No-Dues Form",
      description: "Fill & submit your No-Dues form once resignation reaches HR",
      icon: "ti-pencil-plus",
      accentColor: "#0F6E56",
      iconBg: "#E2F3EE",
      route: routes.Esepration.noDuesForm,
      category: "employee",
    },
    {
      label: "No-Dues Clearance",
      description: "Track department-wise no-dues clearance status",
      icon: "ti-checklist",
      accentColor: "#1D6E5F",
      iconBg: "#E2F2EF",
      route: routes.Esepration.noDues,
      category: "employee",
    },
    {
      label: "View Final Details",
      description: "Consolidated view & documents after separation",
      icon: "ti-file-certificate",
      accentColor: "#185FA5",
      iconBg: "#E5F0FA",
      route: routes.Esepration.finalDetails,
      category: "employee",
    },
    {
      label: "Employee Profile / Biodata",
      description: "View qualifications, experience & download official Biodata PDF",
      icon: "ti-user-circle",
      accentColor: "#1a1f36",
      iconBg: "#E9EAEF",
      route: routes.Esepration.profile,
      category: "employee",
    },
    {
      label: "Pending Approvals",
      description: "Review and action separation requests pending your level",
      icon: "ti-inbox",
      accentColor: "#185FA5",
      iconBg: "#E5F0FA",
      route: routes.Esepration.approvals,
      category: "approver",
    },
    {
      label: "HR Verification & Processing",
      description: "Verify vigilance, D&AR, bond info & generate acceptance letters",
      icon: "ti-clipboard-check",
      accentColor: "#BA7517",
      iconBg: "#FFF1DE",
      route: routes.Esepration.hrVerification,
      category: "hr",
    },
    {
      label: "Initiate Separation Case",
      description:
        "Raise a termination, death, deputation, retirement or transfer-out case (No-Dues only)",
      icon: "ti-briefcase",
      accentColor: "#C0392B",
      iconBg: "#FBE4E0",
      route: routes.Esepration.hrInitiateCase,
      category: "hr",
    },
    {
      label: "Initiated Cases",
      description: "Track the No-Dues-only separation cases (all types) and their No-Dues status",
      icon: "ti-list",
      accentColor: "#185FA5",
      iconBg: "#E6F1FB",
      route: routes.Esepration.hrInitiatedCases,
      category: "hr",
    },
    // {
    //   label: "HR No-Dues Initiation",
    //   description: "Manually initiate No-Dues for non-resignation separations",
    //   icon: "ti-player-play",
    //   accentColor: "#993C1D",
    //   iconBg: "#FBE9E2",
    //   route: routes.Esepration.noDuesInitiate,
    //   category: "hr",
    // },
    {
      label: "HR No-Dues Final Review",
      description: "Consolidated department clearance & final release sign-off",
      icon: "ti-checkup-list",
      accentColor: "#0F6E56",
      iconBg: "#E2F3EE",
      route: routes.Esepration.noDuesHrReview,
      category: "hr",
    },
    {
      label: "No-Dues Departments",
      description: "Configure No-Dues departments & their nodal approvers",
      icon: "ti-building-community",
      accentColor: "#4A1A6E",
      iconBg: "#F0E7F6",
      route: routes.Esepration.noDuesDeptConfig,
      category: "hr",
    },
    {
      label: "Bond Types",
      description: "Configure bond types & the fields employees fill in for each",
      icon: "ti-shield-check",
      accentColor: "#993C1D",
      iconBg: "#FDE8E0",
      route: routes.Esepration.bondTypeConfig,
      category: "hr",
    },
    {
      label: "Vigilance Officer",
      description: "Assign who receives resignations sent to Vigilance",
      icon: "ti-user-shield",
      accentColor: "#4A1A6E",
      iconBg: "#F0E7F6",
      route: routes.Esepration.vigilanceConfig,
      category: "hr",
    },
    {
      label: "No-Dues Dept Clearance",
      description: "Record clearance / dues for No-Dues requests on your desk",
      icon: "ti-clipboard-list",
      accentColor: "#993C1D",
      iconBg: "#FBE9E2",
      route: routes.Esepration.noDuesDeptClearance,
      category: "clearance",
    },
    {
      label: "Nodal Officer Action",
      description: "Process department-wise No-Dues checklists & recovery items",
      icon: "ti-building",
      accentColor: "#BA7517",
      iconBg: "#FFF1DE",
      route: routes.Esepration.noDuesNodal,
      category: "clearance",
    },
    {
      label: "Vigilance Clearance",
      description: "Verify pending vigilance cases & grant/hold clearance",
      icon: "ti-shield-check",
      accentColor: "#4A1A6E",
      iconBg: "#F0E7F6",
      route: routes.Esepration.noDuesVigilance,
      category: "clearance",
    },
    // Hidden for now (Finance nodal): the "Finance Clearance" tab. Finance still
    // acts through "Finance Settlement Action" below.
    // {
    //   label: "Finance Clearance",
    //   description: "Process pending loans, advances and recovery amounts",
    //   icon: "ti-cash",
    //   accentColor: "#049123",
    //   iconBg: "#E4F7E8",
    //   route: routes.Esepration.noDuesFinance,
    //   category: "clearance",
    // },
    {
      label: "Finance Settlement Action",
      description: "Settle consolidated department-wise recovery & close No-Dues",
      icon: "ti-cash",
      accentColor: "#993C1D",
      iconBg: "#FDE8E0",
      route: routes.Esepration.noDuesSettlementInbox,
      category: "clearance",
    },
  ];

  // Which categories each role can see.
  const roleCategories: Record<RoleKey, OptionCard["category"][]> = {
    Employee: ["employee"],
    Approver: ["approver"],
    "HR Admin": ["hr"],
    Nodal: ["clearance"],
    Vigilance: ["clearance"],
    Finance: ["clearance"],
  };

  // Profile/Biodata is visible to all roles.
  const visibleCards = cards.filter((c) => {
    if (c.label === "Employee Profile / Biodata") return true;
    const allowed = roleCategories[role];
    if (!allowed.includes(c.category)) return false;
    if (role === "Nodal")
      return (
        // c.label === "Nodal Officer Action" ||   // hidden for now
        c.label === "No-Dues Dept Clearance"
      );
    if (role === "Vigilance") return c.label === "Vigilance Clearance";
    if (role === "Finance")
      return (
        // c.label === "Finance Clearance" ||   // hidden for now
        c.label === "Finance Settlement Action"
      );
    return true;
  });

  // Map "HR Admin" → "HR" when passing to screens.
  const mappedRole = role === "HR Admin" ? "HR" : role;

  const goTo = (route: string) => {
    if (route === routes.Esepration.noDuesForm && emp?.eligible_resignation_id) {
      navigate(route, { state: { request_id: emp.eligible_resignation_id } });
      return;
    }
    if (route === routes.Esepration.shortNotice) {
      navigate(route, {
        state: { role: mappedRole, request_id: emp?.active_resignation_id ?? undefined },
      });
      return;
    }
    navigate(route, { state: { role: mappedRole } });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="E-Separation"
          subtitle="Manage resignation, clearance & separation processes"
          onExit={() => navigate("/")}
        />

        {/* Role simulator tab strip */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-2">
            <div className="d-flex flex-wrap gap-2">
              {roles.map((r) => (
                <button
                  key={r}
                  className={`btn btn-sm ${role === r ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setRole(r)}
                >
                  {r}
                  <CountBadge n={roleCount[r]} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {role === "Employee" && emp?.access_locked && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
            <i className="ti ti-lock mt-1" />
            <div>
              <div className="fw-semibold">E-Separation is not available</div>
              <div className="small">
                {emp.access_locked_reason ||
                  "This separation is handled by HR. E-Separation is not available to you."}
              </div>
            </div>
          </div>
        )}

        <div className="row g-3">
          {visibleCards.map((c) => {
            const { disabled, message } = getDisabledInfo(c.route, c.category);
            return (
              <div className="col-md-6 col-xl-4" key={c.label}>
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{
                    borderRadius: 14,
                    borderLeft: `4px solid ${disabled ? "#9a9a9a" : c.accentColor}`,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                  }}
                  onClick={() =>
                    disabled
                      ? showDisabled(message)
                      : goTo(c.route)
                  }
                >
                  <div className="card-body d-flex align-items-center gap-3">
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: 12,
                        background: disabled ? "#F0F0F0" : c.iconBg,
                        color: disabled ? "#9a9a9a" : c.accentColor,
                      }}
                    >
                      <i className={`ti ${c.icon}`} style={{ fontSize: 22 }} />
                    </span>
                    <div className="flex-grow-1">
                      <div
                        className="fw-semibold d-flex align-items-center gap-2"
                        style={disabled ? { color: "#9a9a9a" } : undefined}
                      >
                        <span>{c.label}</span>
                        <CountBadge n={cardCount[c.label] || 0} />
                        {disabled && (
                          <span
                            className="badge bg-secondary-subtle text-secondary"
                            style={{ fontSize: 10, fontWeight: 500 }}
                          >
                            Disabled
                          </span>
                        )}
                      </div>
                      <small
                        className="text-muted d-block"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {c.description}
                      </small>
                    </div>
                    <i className="ti ti-chevron-right text-muted" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EseprationHub;
